import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { QuizRunner } from '@/features/Quiz/QuizRunner';
import { QuizShell } from '@/features/Quiz/QuizShell';
import { useTopicDetail } from '@/lib/hooks/useLms';
import { assessmentsApi, type MCQQuestionStudent, type AssessmentSubmission, type QuizInfo } from '@/lib/api/assessments';
import { usePreviewStore } from '@/stores/previewStore';

/**
 * The trailing underscore on the $topicId_ directory is load-bearing: it opts
 * this route out of the topics/$topicId layout while keeping the URL exactly
 * as it was. Exempting the route from AppLayout alone was not enough — the
 * lesson header, the seven-tab nav and the curriculum rail come from
 * TopicShell in that parent route, not from the app layout.
 *
 * `from` records which list launched the attempt, so the way out can be
 * labelled with it. Nothing recorded this before: QuizRunner had no back
 * affordance of any kind, and a student who arrived from Assessments was
 * dropped into the topic lesson shell with no route back to where they were.
 */
const searchSchema = z.object({
  from: z.enum(['practice', 'topic']).optional(),
});

export const Route = createFileRoute('/_student/courses/$batchId/topics/$topicId_/quiz/$assessmentId')({
  validateSearch: searchSchema,
  component: QuizPage,
});

const LETTERS = ['A', 'B', 'C', 'D'];

/**
 * QuizPage routes between two quiz engines.
 *
 * Which one is authoritative is decided by the SERVER, via the read-only
 * `quiz-info` probe: `attempt_managed` mirrors the exact predicate the backend
 * uses to refuse legacy `submitMCQ` with a 409, so the two can never disagree.
 * It is not inferred client-side, and it is deliberately not discovered by
 * POSTing an attempt — `StartAttempt` falls back to `mcq_questions` when there
 * is no pool, so probing that way would create an attempt row and permanently
 * convert one of the 2500-odd legacy untimed quizzes to the attempt lifecycle.
 *
 * If the probe itself fails we fall through to the legacy path: that is the
 * pre-existing behaviour, and on a genuinely attempt-managed quiz the legacy
 * submit answers with a loud 409 rather than silently grading the wrong thing.
 */
function QuizPage() {
  const { assessmentId, batchId, topicId } = Route.useParams();
  const { from } = Route.useSearch();
  const title = useQuizTitle(topicId, assessmentId);
  const [info, setInfo] = useState<QuizInfo | null>(null);
  const [probing, setProbing] = useState(true);
  const previewActive = usePreviewStore((s) => s.active);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const i = await assessmentsApi.getQuizInfo(assessmentId);
        if (!cancelled) setInfo(i);
      } catch {
        /* fall through to the legacy path */
      } finally {
        if (!cancelled) setProbing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  const body = probing ? (
    <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">Loading quiz…</div>
  ) : info?.attempt_managed ? (
    <QuizRunner assessmentID={assessmentId} info={info} previewActive={previewActive} />
  ) : (
    <LegacyQuiz assessmentId={assessmentId} previewActive={previewActive} />
  );

  return (
    <QuizShell title={title} origin={from} batchId={batchId} topicId={topicId}>
      {body}
    </QuizShell>
  );
}

/**
 * The quiz's own title, read off the topic it belongs to.
 *
 * QuizInfo carries timing and pool metadata but no title, and the practice
 * detail endpoint is not used for quizzes. The topic detail definitely
 * contains this assessment and is usually already in cache from the page the
 * student came from.
 */
function useQuizTitle(topicId: string, assessmentId: string): string {
  const { data: topic } = useTopicDetail(topicId);
  return topic?.assessments?.find((a) => a.id === assessmentId)?.title ?? 'Quiz';
}

/**
 * LegacyQuiz is the original untimed one-shot path, unchanged: all questions on
 * one page, a positional answers array, and a single graded submission. It
 * still serves every assessment with no timing policy and no question pool.
 */
function LegacyQuiz({ assessmentId, previewActive }: { assessmentId: string; previewActive: boolean }) {
  const [questions, setQuestions] = useState<MCQQuestionStudent[]>([]);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submission, setSubmission] = useState<AssessmentSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    assessmentsApi.getMySubmission(assessmentId)
      .then(sub => setSubmission(sub))
      .catch(() => {}); // 404 = not submitted yet

    assessmentsApi.listQuestionsForStudent(assessmentId)
      .then(setQuestions)
      .catch(() => toast.error('Failed to load quiz'))
      .finally(() => setLoading(false));
  }, [assessmentId]);

  const handleSubmit = async () => {
    if (Object.keys(answers).length !== questions.length) {
      toast.error('Please answer all questions before submitting');
      return;
    }
    const answersArr = questions.map(q => answers[q.id] ?? 0);
    setSubmitting(true);
    try {
      const sub = await assessmentsApi.submitMCQ(assessmentId, { answers: answersArr });
      setSubmission(sub);
      toast.success('Quiz submitted!');
    } catch { toast.error('Failed to submit quiz'); }
    finally { setSubmitting(false); }
  };

  const maxScore = questions.reduce((s, q) => s + q.points, 0);

  if (loading) return <div className="flex h-64 items-center justify-center text-muted-foreground text-sm">Loading quiz…</div>;
  if (questions.length === 0) return <div className="flex h-64 items-center justify-center text-muted-foreground text-sm">This quiz has no questions yet.</div>;

  if (submission) {
    const percent = submission.max_score > 0 ? Math.round((submission.score / submission.max_score) * 100) : 0;
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <div className="rounded-xl border border-border bg-card p-6 mb-6">
          <h2 className="text-xl font-bold mb-1">{percent >= 80 ? '🎉' : percent >= 50 ? '👍' : '📚'} Quiz Complete</h2>
          <p className="text-sm text-muted-foreground mb-4">{new Date(submission.submitted_at).toLocaleString()}</p>
          <div className="flex gap-8">
            <div>
              <div className={`text-3xl font-bold ${percent >= 70 ? 'text-green-500' : percent >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>{submission.score}/{submission.max_score}</div>
              <div className="text-xs text-muted-foreground">Score</div>
            </div>
            <div>
              <div className={`text-3xl font-bold ${percent >= 70 ? 'text-green-500' : percent >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>{percent}%</div>
              <div className="text-xs text-muted-foreground">Percentage</div>
            </div>
          </div>
        </div>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Your Answers</h3>
        <div className="space-y-3">
          {questions.map((q, idx) => {
            const chosen = submission.answers?.[idx];
            return (
              <div key={q.id} className="rounded-lg border border-border bg-card p-4">
                <p className="text-sm font-semibold mb-3">Q{idx + 1}. {q.question_text}</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {q.options.map((opt, i) => (
                    <div key={i} className={`text-xs px-3 py-1.5 rounded-md border ${i === chosen ? 'border-blue-500 bg-blue-500/10 text-blue-600 dark:text-blue-400' : 'border-border text-muted-foreground'}`}>
                      {LETTERS[i]}. {opt}{i === chosen ? ' (your answer)' : ''}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
      <h1 className="text-xl font-bold mb-1">MCQ Quiz</h1>
      <p className="text-sm text-muted-foreground mb-6">{questions.length} questions · {maxScore} points total</p>
      <div className="space-y-4">
        {questions.map((q, idx) => (
          <div key={q.id} className="rounded-xl border border-border bg-card p-5">
            <p className="text-sm font-semibold mb-4">{idx + 1}. {q.question_text}</p>
            <div className="space-y-2">
              {q.options.map((opt, i) => (
                <label key={i} className={`flex min-h-[44px] items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer text-sm transition-colors ${answers[q.id] === i ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}>
                  <input type="radio" name={`q_${q.id}`} checked={answers[q.id] === i} onChange={() => setAnswers(prev => ({ ...prev, [q.id]: i }))} className="h-4 w-4 shrink-0 accent-primary" />
                  <span>{LETTERS[i]}. {opt}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{q.points} pt{q.points !== 1 ? 's' : ''}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 mt-6">
        <span className="text-sm text-muted-foreground">{Object.keys(answers).length}/{questions.length} answered</span>
        <button onClick={handleSubmit} disabled={submitting || previewActive} title={previewActive ? 'Not available in student preview' : undefined} className="min-h-[44px] rounded-lg bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-60">
          {submitting ? 'Submitting…' : 'Submit Quiz'}
        </button>
      </div>
    </div>
  );
}
